import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getHashtags, updateHashtag, type Hashtag } from '../api/client';
import { formatDistanceToNow } from 'date-fns';
import { clsx } from 'clsx';

const typeColors: Record<string, string> = {
  customer: 'bg-blue-100 text-blue-700',
  product: 'bg-purple-100 text-purple-700',
  engagement: 'bg-green-100 text-green-700',
  uncategorized: 'bg-gray-100 text-gray-600',
};

const types = ['customer', 'product', 'engagement', 'uncategorized'];

export default function Hashtags() {
  const queryClient = useQueryClient();
  const { data: hashtags, isLoading } = useQuery({
    queryKey: ['hashtags'],
    queryFn: getHashtags,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, type }: { id: string; type: string }) =>
      updateHashtag(id, type),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hashtags'] });
    },
  });

  const sortedHashtags = [...(hashtags || [])].sort(
    (a, b) => b.usage_count - a.usage_count
  );

  const uncategorizedCount = hashtags?.filter(
    (h) => h.type === 'uncategorized'
  ).length;

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Hashtags</h1>
        <p className="text-gray-500 mt-1">Categorize and manage hashtags</p>
      </div>

      {uncategorizedCount && uncategorizedCount > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
          <p className="text-yellow-800">
            ⚠️ {uncategorizedCount} hashtag{uncategorizedCount > 1 ? 's' : ''} need
            categorization
          </p>
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-12 text-gray-500">Loading...</div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Hashtag
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Usage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Used
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedHashtags.map((hashtag: Hashtag) => (
                <tr
                  key={hashtag.id}
                  className={clsx(
                    hashtag.type === 'uncategorized' && 'bg-yellow-50'
                  )}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <span className="text-sm font-medium text-primary-600">
                        #{hashtag.name}
                      </span>
                      {hashtag.display_name && (
                        <p className="text-xs text-gray-500">
                          {hashtag.display_name}
                        </p>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={clsx(
                        'px-2 py-1 rounded text-xs font-medium',
                        typeColors[hashtag.type]
                      )}
                    >
                      {hashtag.type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {hashtag.usage_count} times
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {hashtag.last_used
                      ? formatDistanceToNow(new Date(hashtag.last_used), {
                          addSuffix: true,
                        })
                      : 'Never'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <select
                      value={hashtag.type}
                      onChange={(e) =>
                        updateMutation.mutate({
                          id: hashtag.id,
                          type: e.target.value,
                        })
                      }
                      className="px-3 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500"
                    >
                      {types.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
